import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {diApiEu} from '@di-shared/api/di';
import type {ApiResponseTripTagListResponse, ApiResponseTripTagsResponse} from '@di-shared/api/di/__generated_eu';
import {ERROR, UNIT} from '@di-shared/const';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import Popper from '@di-shared/features/common/Popper';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {HistoryTripDetailProps} from '@di-shared/features/history/trip-detail/HistoryTripDetail';
import {ROUTE_PATHS} from '@di-shared/routes';
import {roundToFixed} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter, {DistanceUnitSymbol} from '@di-shared/utils/useUnitConverter';
import {DistanceUnitType} from '@shared/cci/features/const/environment';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useQueryClient, useSuspenseQueries, useSuspenseQuery} from '@tanstack/react-query';

export default function HistoryTripDetailEu({vin, tripId}: HistoryTripDetailProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          tripId={tripId}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

const MINUTES_PER_HOUR = 60;

function Inner({vin, tripId}: HistoryTripDetailProps) {
  const {t} = useInferableTranslation();
  const {format: formatCldrDate} = useCldrDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol, convertSpeedValue, convertSpeedSymbol, isDistanceLessThan} = useUnitConverter();
  const {distanceUnit: appDistanceUnitName} = environmentStore.getState();
  const {openConfirm, backOrClose} = useCcWebviewUtilsHelper();

  const tripIdNumber = Number(tripId);

  const {data: tripDetail} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveTripDetail), vin, tripIdNumber],
    queryFn: () => diApiEu.drivingHistory.retrieveTripDetail({vin, tripId: tripIdNumber, displayOffsetDateTime: true}),
    select: ({data}) => data,
  });

  const [deleteTrip] = usePendingHandler(
    async () => {
      const isConfirm = await openConfirm({
        title: t('msg_mytrips_details_delete_popup_title_eu'),
        message: `${t('msg_mytrips_details_delete_popup_cannot_restore_eu')}\n${t('msg_mytrips_details_delete_popup_not_affect_eu')}`,
        okText: t('ctn_mytrips_details_delete_popup_delete_eu'),
        cancelText: t('ctn_mytrips_details_delete_popup_cancel_eu'),
      });

      if (!isConfirm) {
        return;
      }

      await diApiEu.drivingHistory.deleteTrip({
        vin,
        tripId: tripIdNumber,
      });
      WebviewUtils.showToast(t('ctn_mytrips_details_delete_toast_complete_eu'));
      backOrClose();
    },
    async () => {
      WebviewUtils.showToast(t('ctn_mytrips_details_delete_toast_fail_eu'));
    }
  );

  const ignitionOnDate = parseDateWithFormat(tripDetail.ignOnTime);
  const ignitionOffDate = parseDateWithFormat(tripDetail.ignOffTime);

  const drivingHours = Math.floor(tripDetail.drvTime / MINUTES_PER_HOUR);
  const drivingMinutes = tripDetail.drvTime % MINUTES_PER_HOUR;

  const headerDate = ignitionOnDate ? formatCldrDate(ignitionOnDate, 'MMMMEEEEd') : ERROR.DASH;
  const startTime = ignitionOnDate ? formatCldrDate(ignitionOnDate, 'aHM') : ERROR.DASH;
  const endTime = ignitionOffDate ? formatCldrDate(ignitionOffDate, 'aHM') : ERROR.DASH;

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{headerDate}</h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section section_trip_detail">
          <div className="driving_summary">
            <dl className="driving_summary_list">
              <div className="driving_summary_item">
                <dt className="driving_summary_title">
                  {t('ctn_mytrips_details_start_end_time_eu')}
                  <HistoryTripDetailTooltip />
                </dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">
                    {startTime} - {endTime}
                  </span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_driving_time_eu')}</dt>
                <dd className="driving_summary_info">
                  {drivingHours > 0 && (
                    <>
                      <span className="driving_summary_value">{drivingHours}</span>
                      <span className="driving_summary_unit">{t('ctn_mytrips_details_time_h_01')}</span>
                    </>
                  )}
                  <span className="driving_summary_value">{drivingMinutes}</span>
                  <span className="driving_summary_unit">{t('ctn_mytrips_details_time_min_01')}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_trip_distance_eu')}</dt>
                <dd className="driving_summary_info">
                  {isDistanceLessThan(tripDetail.drvDist) ? (
                    <span className="driving_summary_value">
                      {t('imsi_less_distance_$unit', {
                        unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                      })}
                    </span>
                  ) : (
                    <>
                      <span className="driving_summary_value">{convertDistanceValue(tripDetail.drvDist)}</span>
                      <span className="driving_summary_unit">{convertDistanceSymbol(tripDetail.drvDist)}</span>
                    </>
                  )}
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_average_speed_eu')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{convertSpeedValue(tripDetail.avgSpeed)}</span>
                  <span className="driving_summary_unit">{convertSpeedSymbol(tripDetail.avgSpeed)}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_top_speed')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{convertSpeedValue(tripDetail.maxSpeed)}</span>
                  <span className="driving_summary_unit">{convertSpeedSymbol(tripDetail.maxSpeed)}</span>
                </dd>
              </div>
              {!tripDetail.evEnergyUsage && (
                <div className="driving_summary_item">
                  <dt className="driving_summary_title">{t('ctn_mytrips_details_idle_time')}</dt>
                  <dd className="driving_summary_info">
                    <span className="driving_summary_value">{tripDetail.idleTime}</span>
                    <span className="driving_summary_unit">{t('ctn_mytrips_details_time_min_01')}</span>
                  </dd>
                </div>
              )}
              {tripDetail.evEnergyUsage && (
                <>
                  <div className="driving_summary_item">
                    <dt className="driving_summary_title">{t('ctn_mytrips_ev_energyuse')}</dt>
                    <dd className="driving_summary_info">
                      <span className="driving_summary_value">
                        {roundToFixed(tripDetail.evEnergyUsage.totalPowerConsumption / 1000, 1)}
                      </span>
                      <span className="driving_summary_unit">{UNIT.KWH}</span>
                    </dd>
                  </div>
                  <div className="driving_summary_item">
                    <dt className="driving_summary_title">{t('ctn_mytrips_ev_regenerative_01')}</dt>
                    <dd className="driving_summary_info">
                      <span className="driving_summary_value">{roundToFixed(tripDetail.evEnergyUsage.regenPower / 1000, 1)}</span>
                      <span className="driving_summary_unit">{UNIT.KWH}</span>
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>
        </section>
        <section className="section section_tag">
          <HistoryTripTagsSection
            vin={vin}
            tripId={tripIdNumber}
          />
          {/**
           * [2026-05-14:Jeyoung Lee] FIXME:
           * 장혜진 Rebecka 매니저 드라이빙인사이트팀
           * 트립 삭제가 화면상 미표출(DB flag 처리)인데 "delete"으로 표현하는 것에 대해 법인에서 우려 표출
           * GDPR 근거해 고객이 데이터 요청 시 법인에서는 CCS 서버에서 데이터 추출해 고객 제공
           * CCS 서버에서 추출할 경우 고객이 delete한 트립이 삭제되지 않고 살아있는 것을 확인하는 상황이 문제가 될 수 있음
           * 이 부분에 대해 법무검토가 필요함 (법인에서 법무검토 진행 예정)
           * 5월 배포 제외
           */}
          {/*<div className="trip_delete_area">*/}
          {/*  <button*/}
          {/*    type="button"*/}
          {/*    className="btn_trip_delete"*/}
          {/*    onClick={deleteTrip}*/}
          {/*  >*/}
          {/*    {t('ctn_mytrips_details_delete_button_eu')}*/}
          {/*  </button>*/}
          {/*</div>*/}
        </section>
      </main>
    </div>
  );
}

function HistoryTripTagsSection({vin, tripId}: {vin: string; tripId: number}) {
  return (
    <ErrorBoundary fallback={<HistoryTripTagsSectionErrorFallback />}>
      <Suspense fallback={<HistoryTripTagsSectionLoading />}>
        <HistoryTripTagsSectionInner
          vin={vin}
          tripId={tripId}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function HistoryTripTagsSectionInner({vin, tripId}: {vin: string; tripId: number}) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {resolveError} = useApiError();

  const [{data: availableTags}, {data: tripTags}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApiEu.tripTags.getAllTags)],
        queryFn: () => diApiEu.tripTags.getAllTags(),
        select: ({data}: ApiResponseTripTagListResponse) => data,
      },
      {
        queryKey: [getQueryKey(diApiEu.tripTags.getTagsForTrip), tripId],
        queryFn: () => diApiEu.tripTags.getTagsForTrip({tripId}),
        select: ({data}: ApiResponseTripTagsResponse) => data,
      },
    ],
  });

  const [addTag] = usePendingHandler(
    async (tagName: string) => {
      await diApiEu.tripTags.addTagToTrip({
        tripId,
        vin,
        tagName,
      });
      await queryClient.invalidateQueries({
        queryKey: [getQueryKey(diApiEu.tripTags.getTagsForTrip), tripId],
      });
      await queryClient.invalidateQueries({
        queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveDailyTripHistory)],
      });
    },
    async (error) => {
      const [execute] = await resolveError(error);
      execute();
    }
  );

  const [removeTag] = usePendingHandler(
    async (tagName: string) => {
      await diApiEu.tripTags.removeTagFromTrip({
        tripId,
        vin,
        tagName,
      });
      await queryClient.invalidateQueries({
        queryKey: [getQueryKey(diApiEu.tripTags.getTagsForTrip), tripId],
      });
      await queryClient.invalidateQueries({
        queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveDailyTripHistory)],
      });
    },
    async (error) => {
      const [execute] = await resolveError(error);
      execute();
    }
  );

  async function toggleTag(tagName: string) {
    const isTagApplied = tripTags.tags.some((tag) => tag.tagName === tagName);

    if (isTagApplied) {
      await removeTag(tagName);
    } else {
      await addTag(tagName);
    }
  }

  return (
    <>
      <HistoryTripTagsSectionHeader />
      <div className="tag_area">
        {availableTags.tags.map((tag) => {
          const isApplied = tripTags.tags.some((t) => t.tagName === tag.tagName);
          return (
            <button
              key={tag.tagName}
              type="button"
              className={isApplied ? 'btn btn_round is-selected' : 'btn btn_round'}
              onClick={() => toggleTag(tag.tagName)}
            >
              <span className="ic ic_check_16px" />
              {tag.tagName}
            </button>
          );
        })}
        <button
          type="button"
          className="btn btn_round btn_tag_add"
          onClick={() => {
            navigate(ROUTE_PATHS.HISTORY_TAGS.getFullPath(vin));
          }}
        >
          <span
            className="ic ic_add_14px"
            aria-hidden="true"
          />
          {t('ctn_mytrips_tags_create_button_eu')}
        </button>
      </div>
    </>
  );
}

function HistoryTripTagsSectionHeader() {
  const {t} = useInferableTranslation();

  return (
    <div className="section_container_header">
      <h3 className="title_xsmall_b">{t('ctn_mytrips_details_tags_title_eu')}</h3>
      <Popper
        placement="bottom"
        className="tooltip"
        marginLeft={1.5}
        tooltip={
          <div
            tabIndex={0}
            role="tooltip"
          >
            <p className="tooltip_text">{t('msg_mytrips_details_tags_tooltip_eu')}</p>
          </div>
        }
      >
        <div className="tooltip_area tooltip_area_single">
          <button
            type="button"
            className="btn_tooltip"
            aria-controls="tooltipStatement01"
            aria-expanded="false"
            aria-describedby="tooltipBox02"
          >
            <span
              className="ic ic_information_16px"
              aria-hidden="true"
            ></span>
            <span className="blind">{t('T10044')}</span>
          </button>
        </div>
      </Popper>
    </div>
  );
}

function HistoryTripTagsSectionLoading() {
  const {t} = useInferableTranslation();

  return (
    <>
      <HistoryTripTagsSectionHeader />
      <div className="tag_area">
        {Array.from({length: 3}).map((_, index) => (
          <span
            key={`tag-skeleton-${index}`}
            className="btn btn_round skeleton"
            style={{width: 72, height: 32}}
            aria-hidden="true"
          />
        ))}
      </div>
      <span className="blind">{t('ctn_mytrips_details_tags_title_eu')}</span>
    </>
  );
}

function HistoryTripTagsSectionErrorFallback() {
  const {t} = useInferableTranslation();

  return (
    <>
      <HistoryTripTagsSectionHeader />
      <div className="trip_info">
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px"></span>
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('msg_mytrips_tags_failed_to_load_eu') + '\n' + t('msg_mytrips_details_try_again_eu')}
          </span>
        </div>
      </div>
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title"></h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section section_trip_detail">
          <div className="driving_summary">
            <dl className="driving_summary_list">
              <div className="driving_summary_item">
                <dt className="driving_summary_title">
                  {t('ctn_mytrips_details_start_end_time_eu')}
                  <HistoryTripDetailTooltip />
                </dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_driving_time_eu')}</dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_trip_distance_eu')}</dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_average_speed_eu')}</dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_top_speed')}</dt>
                <dd
                  className="driving_summary_info skeleton"
                  style={{width: 100}}
                >
                  <span className="driving_summary_value">&nbsp;</span>
                </dd>
              </div>
            </dl>
          </div>
        </section>
        <section className="section section_tag">
          <HistoryTripTagsSectionLoading />
        </section>
      </main>
    </div>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol, defaultSpeedSymbol} = useUnitConverter();
  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{ERROR.DASH}</h1>
      </Header>
      <main
        id="content"
        className="content page_my_trips"
      >
        <section className="section section_trip_detail">
          <div className="driving_summary">
            <dl className="driving_summary_list">
              <div className="driving_summary_item">
                <dt className="driving_summary_title">
                  {t('ctn_mytrips_details_start_end_time_eu')}
                  <HistoryTripDetailTooltip />
                </dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_driving_time_eu')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{t('ctn_mytrips_details_time_h_01')}</span>
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{t('ctn_mytrips_details_time_min_01')}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_trip_distance_eu')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{defaultDistanceSymbol}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_average_speed_eu')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{defaultSpeedSymbol}</span>
                </dd>
              </div>
              <div className="driving_summary_item">
                <dt className="driving_summary_title">{t('ctn_mytrips_details_top_speed')}</dt>
                <dd className="driving_summary_info">
                  <span className="driving_summary_value">{ERROR.DASH}</span>
                  <span className="driving_summary_unit">{defaultSpeedSymbol}</span>
                </dd>
              </div>
            </dl>
          </div>
          <div className="trip_info">
            <div className="empty_area">
              <span className="ic ic_caution_32px"></span>
              <span
                className="body_small_r"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('msg_mytrips_details_fail_load_data_eu') + '\n' + t('msg_mytrips_details_try_again_eu')}
              </span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function HistoryTripDetailTooltip() {
  const {t} = useInferableTranslation();
  return (
    <Popper
      placement="bottom"
      className="tooltip"
      marginLeft={3}
      tooltip={
        <div
          role="tooltip"
          tabIndex={0}
        >
          <p
            className="tooltip_text"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('msg_mytrips_monthly_distance_tooltip_eu')}
          </p>
        </div>
      }
    >
      <div className="tooltip_area tooltip_area_single">
        <button
          className="btn_tooltip"
          aria-expanded="false"
          type="button"
        >
          <span
            className="ic ic_information_16px"
            aria-hidden="true"
          />
        </button>
      </div>
    </Popper>
  );
}
